import { ArrowLeft, ArrowRight, FileSpreadsheet } from "lucide-react";
import React, { useState, useMemo } from "react";
import SearchableDropdown from "../Buttons/SearchableDropdown";
import TableShimmer from "../Shimmers/TableShimmer";

const getPaginationRange = (currentPage, totalPages) => {
  const range = [];

  if (totalPages <= 5) {
    // small pages → show all
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // always show first page
  range.push(1);

  if (currentPage > 3) {
    range.push("...");
  }

  // middle pages
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    range.push(i);
  }

  if (currentPage < totalPages - 2) {
    range.push("...");
  }

  // always show last page
  range.push(totalPages);

  return range;
};

const Datatable = ({
  title = "No Title",
  columns,
  isloading,
  data = [],
  searchKeys = [],
  paginationInfo = null,
}) => {
  const [search, setSearch] = useState("");
  const currentPage = paginationInfo?.currentPage || 1;
  const totalPages = paginationInfo?.totalPages || 1;

  const handlePageChange = (page) => {
    if (paginationInfo?.onPageChange) paginationInfo.onPageChange(page);
  };
  const handleEntriesChange = (e) => {
    const val = Number(e.target.value);
    // console.log("toggle value :", paginationInfo?.entriesPerPage ?? 10);

    // Call parent callback if available
    if (paginationInfo?.onEntriesChange) {
      paginationInfo.onEntriesChange(val);
      // console.log("On entries change called with:", val);
    }

    // console.log("Page info is :", paginationInfo);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (paginationInfo?.onSearchChange) paginationInfo.onSearchChange(value);
  };

  return (
    <div className=" ">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-xl sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl text-gray-800 font-semibold">
            {title}
          </h2>
        </div>
        {/* Button for dropdown and export */}
        {/* <div className="flex gap-2">
           
          <div className="flex gap-1 bg-transparent hover:bg-[#000000] border hover:text-white cursor-pointer py-2 px-2 transition-all ease-in-out  rounded-md duration-300">
            <FileSpreadsheet size={18} />
            <button className="text-xs font-bold">Export</button>
          </div>
        </div> */}
      </div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
        {/* Left: Pagination Info */}
        <div className="text-sm text-gray-600">
          <p>
            Showing page {currentPage} of {totalPages} —{" "}
            {paginationInfo?.entriesPerPage ?? 0} Users
          </p>
        </div>

        {/* Right: Search */}
        {searchKeys.length > 0 && (
          <div className="flex items-center gap-2 w-full md:w-auto">
            <label htmlFor="search" className="text-sm whitespace-nowrap">
              Search:
            </label>
            <input
              id="search"
              type="text"
              className="px-3 py-2 border rounded-md focus:outline-none w-full md:w-64"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search users..."
            />
          </div>
        )}
      </div>
      {/* Table */}
      <div className="relative border border-[#eaebed] rounded-lg max-w-full overflow-x-auto scrollbar-thin z-0">
        {/* TABLE */}
        {isloading ? (
          <TableShimmer
            columns={columns}
            rows={paginationInfo?.entriesPerPage || 10}
          />
        ) : (
          <table className="min-w-[1200px] w-full text-sm text-gray-700">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    className="px-3 py-1 border border-[#eaebed] text-left max-w-md"
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {data.length > 0 ? (
                data.map((row, idx) => (
                  <tr
                    key={row.id || `row-${idx}`}
                    className={`
                    ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    ${row.status === false ? "opacity-50" : ""}
                  `}
                  >
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className={`
                          px-3 py-2 border border-[#eaebed] max-w-md break-word
                          ${
                            row.status === false && col.accessor !== "status" // ✅ toggle column active rahe
                              ? "pointer-events-none cursor-not-allowed"
                              : ""
                          }
                        `}
                      >
                        {col.render
                          ? col.render(row[col.accessor], row)
                          : row[col.accessor]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    No match found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination */}
      {paginationInfo && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-3 gap-2">
          <div className="flex items-center space-x-1">
            <button
              className="px-2 py-1.5 text-sm border border-[#eaebed] rounded bg-white disabled:bg-[#eaeff4] disabled:opacity-50 cursor-pointer flex justify-center items-center"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <ArrowLeft className="w-4 h-4 mr-1 inline" />
            </button>

            {getPaginationRange(currentPage, totalPages).map((page, index) =>
              page === "..." ? (
                <span
                  key={index}
                  className="px-2 py-1 text-sm text-gray-500 select-none"
                >
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 rounded-md text-sm cursor-pointer transition ${
                    currentPage === page
                      ? "bg-orange-500 text-white shadow-sm"
                      : "bg-[#eaeff4] hover:bg-[#FF6943]/10"
                  }`}
                >
                  {page}
                </button>
              ),
            )}

            <button
              className="px-2 py-1.5 text-sm border border-[#eaebed] rounded bg-white disabled:bg-[#eaeff4] disabled:opacity-50 cursor-pointer flex justify-center items-center"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <ArrowRight className="w-4 h-4 mr-1 inline" />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Show</span>
            <div className="relative">
              <select
                className="appearance-none border border-gray-300 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 cursor-pointer
                 focus:outline-none focus:ring-1 focus:ring-[#FF6943] focus:border-[#FF6943]
                 shadow-sm hover:border-[#FF6943] transition-colors duration-150"
                value={paginationInfo?.entriesPerPage ?? 10}
                onChange={handleEntriesChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              {/* Custom dropdown arrow */}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <svg
                  className="h-3 w-3 text-[#FF6943]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <span className="text-sm font-medium text-gray-700">entries</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Datatable;
